import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Loader2, Search, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { adminTranslations, autoTranslate, crud } from "@/lib/apiData";

const api = crud("translations");

const Row = ({ row, onSaved }) => {
  const [f, setF] = useState({ nl: row.nl || "", de: row.de || "" });
  const dirty = f.nl !== (row.nl || "") || f.de !== (row.de || "");
  const save = () => api.update(row.id, f).then(() => { toast.success("Translation saved"); onSaved(); }).catch(() => toast.error("Could not save"));
  return (
    <div className="grid md:grid-cols-[1fr_1fr_1fr_auto] gap-3 px-5 py-4 items-start" data-testid="translation-row">
      <p className="text-sm text-neutral-900 pt-2">{row.text}</p>
      {["nl", "de"].map((l) => (
        <div key={l}>
          <Textarea rows={2} value={f[l]} onChange={(e) => setF({ ...f, [l]: e.target.value })} placeholder={l === "nl" ? "Dutch" : "German"} data-testid={`translation-${l}`}
            className="bg-[#fbf9f7] border-neutral-300 rounded-none text-sm min-h-[44px] focus-visible:ring-[#d00000]" />
          {row[`${l}_auto`] && f[l] === row[l] && <span className="text-[10px] uppercase tracking-widest text-neutral-400">AI translated</span>}
        </div>
      ))}
      <Button onClick={save} disabled={!dirty} data-testid="translation-save" variant="ghost" className="border border-neutral-300 rounded-none h-10 w-10 p-0" aria-label="Save"><Check className="w-4 h-4" /></Button>
    </div>
  );
};

export default function Translations() {
  const qc = useQueryClient();
  const { data: rows = [] } = useQuery({ queryKey: ["admin-translations"], queryFn: adminTranslations });
  const [q, setQ] = useState("");
  const [onlyMissing, setOnlyMissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-translations"] });
  const missing = rows.filter((r) => !r.nl || !r.de).length;
  const shown = useMemo(() => rows.filter((r) => (!onlyMissing || !r.nl || !r.de) && (!q || `${r.text} ${r.nl} ${r.de}`.toLowerCase().includes(q.toLowerCase()))).slice(0, 200), [rows, q, onlyMissing]);

  const runAuto = async () => {
    setBusy(true);
    try {
      const res = await autoTranslate();
      toast.success(res.running ? "Translation already running — check back shortly" : `Translated ${res.translated} texts. ${res.remaining} left.`);
      refresh();
      qc.invalidateQueries({ queryKey: ["i18n"] });
    } catch {
      toast.error("Translation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-translations-panel">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl font-black text-neutral-900">Translations</h2>
          <p className="text-neutral-500 text-sm mt-1 max-w-2xl">New website text is collected and translated by AI into Dutch and German automatically. Correct any wording here. {rows.length} texts · {missing} waiting.</p>
        </div>
        <Button onClick={runAuto} disabled={busy || missing === 0} data-testid="auto-translate-btn" className="bg-[#d00000] hover:bg-[#a30000] text-white rounded-none gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Translate missing now
        </Button>
      </div>
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search text" data-testid="translation-search" className="pl-9 h-10 bg-white border-neutral-300 rounded-none" />
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-600"><input type="checkbox" checked={onlyMissing} onChange={(e) => setOnlyMissing(e.target.checked)} data-testid="translation-only-missing" className="accent-[#d00000]" /> Only missing</label>
      </div>
      <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
        <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_auto] gap-3 px-5 py-3 text-xs uppercase tracking-widest text-neutral-400"><span>English</span><span>Dutch</span><span>German</span><span className="w-10" /></div>
        {shown.map((r) => <Row key={`${r.id}-${r.nl}-${r.de}`} row={r} onSaved={refresh} />)}
        {shown.length === 0 && <p className="p-8 text-center text-neutral-400">No texts yet. Browse the website and they'll appear here.</p>}
      </div>
    </div>
  );
}
